import React, {forwardRef, type ReactNode, useCallback, useContext, useEffect, useImperativeHandle, useRef} from 'react';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $setSelection,
    COMMAND_PRIORITY_HIGH,
    INSERT_LINE_BREAK_COMMAND,
    KEY_ENTER_COMMAND,
    LineBreakNode,
    ParagraphNode,
    TextNode,
} from 'lexical';
import {Box, type PopperProps} from '@mui/material';
import {EntityPlaceholderContext, EntityPlaceholderProvider} from './entityPlaceholderContext.tsx';
import {$insertDraftText, $insertPromptWithEntities, draftTextContainsEntities, EntityPlaceholderNode} from './entityPlaceholderNode.tsx';
import {SlashCommandPlugin} from './slashCommandPlugin';
import {
    SLASH_ENTITY_TEXT_COLOR,
    splitTextWithSlashEntities,
} from './slashCommandLexical';

export type ChatLexicalInputHandle = {
    focus: () => void;
    blur: () => void;
    getText: () => string;
    clear: () => void;
    setPlainText: (text: string, options?: {highlightSlashEntities?: boolean; selection?: 'start' | 'end' | 'none'}) => void;
    /** Restores draft text with entity chips for `[slot]` and resolved `kind:\`value\`` segments. */
    restoreDraftText: (text: string) => void;
    /** Parses `[label]` segments into inline entity chips and opens the first entity’s picker. */
    insertPromptWithEntities: (prompt: string) => void;
};

type ChatLexicalEditorProps = {
    placeholder: ReactNode;
    disabled?: boolean;
    /** Approximate textarea `rows` for min-height. */
    maxRows: number;
    onTextChange: (trimmedLength: number) => void;
    /** Called when user presses Enter (no Shift); parent reads text via ref. */
    onEnterSubmit: () => void;
    position: Pick<PopperProps, "placement">;
};

function ChatRichTextErrorBoundary({children}: {children: React.ReactElement}) {
    return (
        <LexicalErrorBoundary onError={(e) => console.error(e)}>
            {children}
        </LexicalErrorBoundary>
    );
}

function EditablePlugin({disabled}: {disabled: boolean}) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        editor.setEditable(!disabled);
    }, [disabled, editor]);
    return null;
}

function EnterSubmitPlugin({onEnterSubmit}: {onEnterSubmit: () => void}) {
    const [editor] = useLexicalComposerContext();
    const onEnterSubmitRef = useRef(onEnterSubmit);
    useEffect(() => {
        onEnterSubmitRef.current = onEnterSubmit;
    }, [onEnterSubmit]);

    useEffect(() => {
        return editor.registerCommand(
            KEY_ENTER_COMMAND,
            (event: KeyboardEvent | null) => {
                if (event === null) {
                    return false;
                }
                if (event.shiftKey) {
                    event.preventDefault();
                    editor.dispatchCommand(INSERT_LINE_BREAK_COMMAND, false);
                    return true;
                }
                event.preventDefault();
                onEnterSubmitRef.current();
                return true;
            },
            COMMAND_PRIORITY_HIGH,
        );
    }, [editor]);
    return null;
}

const PREVIEW_CLASS = 'chat-lexical-editable--preview';

function EditorBridgePlugin({
                                forwardedRef,
                            }: {
                                forwardedRef: React.ForwardedRef<ChatLexicalInputHandle | null>;
                            }) {
    const [editor] = useLexicalComposerContext();
    const {setAutoOpenNodeKey, clearAutoOpen} = useContext(EntityPlaceholderContext);

    const setPreviewMode = useCallback((enabled: boolean) => {
        editor.getRootElement()?.classList.toggle(PREVIEW_CLASS, enabled);
    }, [editor]);

    useEffect(() => {
        const exitPreview = () => setPreviewMode(false);
        return editor.registerRootListener((rootElement, prevRootElement) => {
            prevRootElement?.removeEventListener('focus', exitPreview);
            rootElement?.addEventListener('focus', exitPreview);
        });
    }, [editor, setPreviewMode]);

    useImperativeHandle(
        forwardedRef,
        () => {
            const setPlainText = (
                plain: string,
                options?: {highlightSlashEntities?: boolean; selection?: 'start' | 'end' | 'none'},
            ) => {
                const selection = options?.selection ?? 'end';
                editor.update(() => {
                    const root = $getRoot();
                    root.clear();
                    const p = $createParagraphNode();
                    if (plain.length > 0) {
                        if (options?.highlightSlashEntities) {
                            for (const segment of splitTextWithSlashEntities(plain)) {
                                const textNode = $createTextNode(segment.text);
                                if (segment.isSlashEntity) {
                                    textNode.setStyle(`color: ${SLASH_ENTITY_TEXT_COLOR}`);
                                }
                                p.append(textNode);
                            }
                        } else {
                            p.append($createTextNode(plain));
                        }
                    }
                    root.append(p);
                    if (selection === 'start') {
                        root.selectStart();
                    } else if (selection === 'end') {
                        root.selectEnd();
                    } else {
                        $setSelection(null);
                    }
                });
                if (selection === 'start' || selection === 'none') {
                    queueMicrotask(() => {
                        const el = editor.getRootElement();
                        if (el) {
                            el.scrollTop = 0;
                            el.scrollLeft = 0;
                        }
                        if (selection === 'none') {
                            setPreviewMode(true);
                            editor.blur();
                        } else {
                            setPreviewMode(false);
                        }
                    });
                } else {
                    setPreviewMode(false);
                }
                clearAutoOpen();
            };

            return {
                focus: () => {
                    setPreviewMode(false);
                    editor.focus();
                },
                blur: () => {
                    editor.blur();
                },
                getText: () => {
                    let text = '';
                    editor.getEditorState().read(() => {
                        text = $getRoot().getTextContent();
                    });
                    return text;
                },
                clear: () => {
                    // discrete: flush synchronously so getText() after clear is empty
                    // (avoids re-persisting the sent draft on session/key switch).
                    editor.update(
                        () => {
                            const root = $getRoot();
                            root.clear();
                            root.append($createParagraphNode());
                        },
                        {discrete: true},
                    );
                    setPreviewMode(false);
                    clearAutoOpen();
                },
                setPlainText,
                insertPromptWithEntities: (prompt: string) => {
                    let firstKey: string | null = null;
                    editor.update(() => {
                        firstKey = $insertPromptWithEntities(prompt);
                    });
                    setPreviewMode(false);
                    setAutoOpenNodeKey(firstKey);
                },
                restoreDraftText: (text: string) => {
                    if (draftTextContainsEntities(text)) {
                        editor.update(() => {
                            $insertDraftText(text);
                        });
                        setPreviewMode(false);
                        clearAutoOpen();
                    } else {
                        setPlainText(text);
                    }
                },
            };
        },
        [editor, setAutoOpenNodeKey, clearAutoOpen, setPreviewMode],
    );

    return null;
}

const editorTheme = {
    paragraph: 'chat-lexical-paragraph',
};

export const ChatLexicalEditor = forwardRef<
    ChatLexicalInputHandle | null,
    ChatLexicalEditorProps
>(function ChatLexicalEditor(
    {placeholder, disabled, maxRows, onTextChange, onEnterSubmit, position},
    ref,
) {
    const initialConfig = {
        namespace: 'ChatInput',
        theme: editorTheme,
        editable: !disabled,
        onError: (error: Error) => {
            console.error(error);
        },
        nodes: [EntityPlaceholderNode, ParagraphNode, TextNode, LineBreakNode],
        editorState: () => {
            const root = $getRoot();
            if (root.getFirstChild() === null) {
                root.append($createParagraphNode());
            }
        },
    };

    const minHeight = maxRows * 24;

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <EntityPlaceholderProvider openPlacement={position}>
                <EditorBridgePlugin forwardedRef={ref}/>
                <EditablePlugin disabled={Boolean(disabled)}/>
                <EnterSubmitPlugin onEnterSubmit={onEnterSubmit}/>
                <OnChangePlugin
                    onChange={(editorState) => {
                        editorState.read(() => {
                            const text = $getRoot().getTextContent();
                            onTextChange(text.trim().length);
                        });
                    }}
                />
                <HistoryPlugin/>
                <SlashCommandPlugin disabled={Boolean(disabled)}/>
                <Box
                    sx={{
                        position: 'relative',
                        padding: '0 42px 0px 0px',
                        marginLeft: '5px',

                    }}
                >
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                className="chat-lexical-editable"
                                {...(typeof placeholder === 'string' && {'aria-label': placeholder})}
                                style={{
                                    maxHeight: '67px',
                                    outline: 'none',
                                    fontSize: '1rem',
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'auto',
                                    ['--chat-lexical-line-clamp' as string]: maxRows,
                                }}
                            />
                        }
                        placeholder={(isEditable) =>
                            isEditable ? (
                                <Box
                                    component="span"
                                    className="chat-lexical-placeholder"
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        // Stays out of the 42px kept free for the send button; one line, truncated.
                                        right: '42px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        pointerEvents: 'none',
                                        color: 'text.secondary',
                                        fontSize: '1rem',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {placeholder}
                                </Box>
                            ) : null
                        }
                        ErrorBoundary={ChatRichTextErrorBoundary}
                    />
                </Box>
            </EntityPlaceholderProvider>
        </LexicalComposer>
    );
});
